import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const KEYS = [
  { key: "rookie", label: "Rookie Tier" },
  { key: "pro", label: "Pro Tier" },
  { key: "hof", label: "Hall of Fame Tier" },
];

export default function AdminTiers() {
  const [tiers, setTiers] = useState({});
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/config");
      const map = {};
      (r.data.tiers || []).forEach((t) => { map[t.key] = t; });
      setTiers(map);
    } catch { toast.error("Failed to load tiers"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (key, field, value) => {
    setTiers((cur) => ({ ...cur, [key]: { ...cur[key], [field]: value } }));
  };

  const setPerks = (key, txt) => set(key, "perks", txt.split("\n").filter(Boolean));

  const save = async (key) => {
    setSaving(key);
    try {
      const t = tiers[key];
      await api.post("/admin/tiers", {
        key,
        name: t.name,
        price: parseFloat(t.price),
        discount_pct: parseFloat(t.discount_pct),
        tagline: t.tagline || "",
        perks: t.perks || [],
        highlight: !!t.highlight,
      });
      toast.success(`${key} saved — Stripe price synced`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(null); }
  };

  return (
    <div>
      <h1 className="font-display text-4xl mb-2">Subscription Tiers</h1>
      <p className="text-zinc-400 text-sm mb-8">Editing a price re-syncs to Stripe automatically (existing subscribers stay on their old price until they resubscribe).</p>

      <div className="space-y-6">
        {KEYS.map(({ key, label }) => {
          const t = tiers[key] || {};
          return (
            <div key={key} className="border border-white/10 rounded p-6 bg-[var(--cl-surface)]" data-testid={`tier-form-${key}`}>
              <div className="text-label text-zinc-500 mb-4">{label}</div>
              <div className="grid md:grid-cols-4 gap-4">
                <Field label="Display Name"><input value={t.name || ""} onChange={(e) => set(key, "name", e.target.value)} data-testid={`tier-name-${key}`} className="input" /></Field>
                <Field label="Price ($/mo)"><input type="number" step="0.01" value={t.price || ""} onChange={(e) => set(key, "price", e.target.value)} data-testid={`tier-price-${key}`} className="input" /></Field>
                <Field label="Discount %"><input type="number" step="1" value={t.discount_pct || 0} onChange={(e) => set(key, "discount_pct", e.target.value)} data-testid={`tier-discount-${key}`} className="input" /></Field>
                <Field label="Highlight?">
                  <select value={t.highlight ? "y" : "n"} onChange={(e) => set(key, "highlight", e.target.value === "y")} className="input">
                    <option value="n">No</option><option value="y">Yes (most popular)</option>
                  </select>
                </Field>
                <Field label="Tagline" className="md:col-span-4">
                  <input value={t.tagline || ""} onChange={(e) => set(key, "tagline", e.target.value)} className="input" />
                </Field>
                <Field label="Perks (one per line)" className="md:col-span-4">
                  <textarea rows={4} value={(t.perks || []).join("\n")} onChange={(e) => setPerks(key, e.target.value)}
                            data-testid={`tier-perks-${key}`} className="input" />
                </Field>
              </div>
              <button onClick={() => save(key)} disabled={saving === key} className="btn-primary mt-4" data-testid={`tier-save-${key}`}>
                {saving === key ? "Saving…" : "Save & Sync to Stripe"}
              </button>
            </div>
          );
        })}
      </div>
      <style>{`.input { width: 100%; background: #000; border: 1px solid rgba(255,255,255,0.1); border-radius: 6px; padding: 8px 12px; color: white; font-size: 0.875rem; }`}</style>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-label text-zinc-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
